// LandingLeadForm — форма заявки на интеграцию (R70, V12 U-36-B).
// Поля (research §3.2): Имя*, Email*, Компания*, Телефон (опц.), «Что мешает сегодня?» (опц.),
// чекбокс согласия 152-ФЗ (обязательный, без преселекта), honeypot-поле `website`.
// Submit → POST /api/leads (source=LANDING). Honeypot заполнен → тихий успех БЕЗ вызова API.
// Успех: инлайн success-блок «Заявка отправлена!» + кнопки «Скачать презентацию» / «Открыть демо-стенд»
// (+ «Мы свяжемся с вами»). Ошибки — по-русски (канон R24), 429 — «Слишком много запросов».

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { leadsApi } from '@/api/leads';
import { extractApiErrorMessage } from '@/lib/apiError';
import { publicOrigin } from '@/config/publicEnv';

const PRESENTATION_PPTX = '/presentations/sled-crm-presentation.pptx';

/** Русская телефонная маска-«мягкая» проверка: +7 900 000-00-00 и похожие форматы. */
const PHONE_RE = /^\+?[0-9][0-9\s()-]{5,}$/;

const leadSchema = z.object({
  name: z.string().trim().min(2, 'Укажите имя (минимум 2 символа)'),
  email: z.string().trim().min(1, 'Укажите email').email('Некорректный email'),
  company: z.string().trim().min(2, 'Укажите компанию (минимум 2 символа)'),
  phone: z
    .string()
    .trim()
    .optional()
    .or(z.literal(''))
    .refine((v) => !v || PHONE_RE.test(v), { message: 'Некорректный телефон' }),
  /** «Что мешает сегодня?» — опционально; уходит в notes лида. */
  pains: z.string().trim().optional().or(z.literal('')),
  /** Согласие на обработку ПД (152-ФЗ) — обязательный явный акт (research §3.3). */
  agree: z.boolean().refine((v) => v === true, { message: 'Подтвердите согласие на обработку персональных данных' }),
  /** Honeypot: человек его не видит/не заполняет; бот — заполнил → silent-success без API. */
  website: z.string().optional().or(z.literal('')),
});

type LeadFormValues = z.infer<typeof leadSchema>;

/** Имя из одного поля «Имя» → firstName + lastName (split по пробелам; нет пробела → lastName null). */
function splitFullName(name: string): { firstName: string; lastName: string | null } {
  const parts = name.trim().split(/\s+/);
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') || null };
}

/** Форма заявки на интеграцию (мутация leadsApi.create; успех — инлайн thank-you). */
export function LandingLeadForm(): JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const { control, handleSubmit } = useForm<LeadFormValues>({
    resolver: zodResolver(leadSchema),
    defaultValues: { name: '', email: '', company: '', phone: '', pains: '', agree: false, website: '' },
    mode: 'onSubmit',
  });

  const mutation = useMutation({
    mutationFn: (values: LeadFormValues) => {
      const { firstName, lastName } = splitFullName(values.name);
      return leadsApi.create({
        title: `${values.name.trim()} — ${values.company.trim()}`,
        firstName,
        lastName,
        email: values.email.trim(),
        phone: values.phone?.trim() || null,
        companyName: values.company.trim(),
        notes: values.pains?.trim() || null,
        source: 'LANDING',
      });
    },
    onSuccess: () => setSubmitted(true),
  });

  /** Человекочитаемое сообщение об ошибке: 429 — rate-limit (канон R24), иначе — message/fallback. */
  const errorMessage = useMemo(() => {
    if (mutation.error) {
      const status = (mutation.error as AxiosError | null)?.response?.status;
      if (status === 429) return 'Слишком много запросов — попробуйте позже';
      return extractApiErrorMessage(mutation.error, 'Не удалось отправить заявку. Попробуйте ещё раз.');
    }
    return null;
  }, [mutation.error]);

  const onSubmit = (values: LeadFormValues): void => {
    // Honeypot заполнен (бот): тихий успех — никакого API-вызова, лид не создаётся.
    const honeypotFilled = (values.website ?? '').trim().length > 0;
    if (honeypotFilled) {
      setSubmitted(true);
      return;
    }
    mutation.mutate(values);
  };

  return (
    <Box component="section" aria-label="Форма заявки">
      {submitted ? (
        <Stack spacing={2} data-testid="lead-form-success">
          <Alert severity="success">Заявка отправлена! Презентация уже ждёт вас</Alert>
          <Typography variant="body1">Мы свяжемся с вами в течение рабочего дня.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button component="a" href={PRESENTATION_PPTX} download variant="contained">
              Скачать презентацию
            </Button>
            <Button component="a" href={publicOrigin} target="_blank" rel="noopener noreferrer" variant="outlined">
              Открыть демо-стенд
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Имя"
                  required
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  inputProps={{ 'aria-label': 'Имя' }}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  required
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  inputProps={{ 'aria-label': 'Email' }}
                />
              )}
            />
            <Controller
              name="company"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Компания"
                  required
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  inputProps={{ 'aria-label': 'Компания' }}
                />
              )}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Телефон"
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? 'Опционально'}
                  inputProps={{ 'aria-label': 'Телефон' }}
                />
              )}
            />
            <Controller
              name="pains"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Что мешает сегодня?"
                  multiline
                  minRows={3}
                  fullWidth
                  inputProps={{ 'aria-label': 'Что мешает сегодня?' }}
                />
              )}
            />
            <Controller
              name="website"
              control={control}
              render={({ field }) => (
                // Honeypot: скрытое поле, человек не должен его увидеть/заполнить (research §3.2).
                <Box
                  sx={{ position: 'absolute', left: '-9999px', height: 0, width: 0, overflow: 'hidden' }}
                  aria-hidden="true"
                >
                  <input
                    data-testid="honeypot-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    {...field}
                  />
                </Box>
              )}
            />
            <Controller
              name="agree"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={
                      <span>
                        Согласен на обработку персональных данных (152-ФЗ).{' '}
                        <MuiLink component={RouterLink} to="/privacy">
                          Политика конфиденциальности
                        </MuiLink>
                      </span>
                    }
                  />
                  {fieldState.error ? (
                    <Typography variant="caption" color="error">
                      {fieldState.error.message}
                    </Typography>
                  ) : null}
                </Box>
              )}
            />
            <Button type="submit" variant="contained" size="large" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Отправляем…
                </>
              ) : (
                'Отправить заявку'
              )}
            </Button>
            <Typography variant="body2" color="text.secondary">
              Нажимая кнопку, вы соглашаетесь на обработку персональных данных (152-ФЗ). Ответим в течение
              рабочего дня.
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
